import { KartStatus, type KartListItem } from '@kangaroo/shared';

export type KartQueueFilter = 'all' | 'service-due' | 'maintenance' | 'active';

export type FleetHealthSummary = {
  total: number;
  active: number;
  inMaintenance: number;
  serviceDue: number;
  serviceSoon: number;
  assigned: number;
};

function maintenanceTone(kart: KartListItem): 'default' | 'muted' | 'warning' {
  const remaining = kart.maintenance.remainingHours;
  if (remaining != null && remaining <= 0) {
    return 'warning';
  }

  if (remaining != null && remaining <= 1) {
    return 'muted';
  }

  return 'default';
}

export function isServiceDue(kart: KartListItem) {
  const tone = maintenanceTone(kart);
  return tone === 'warning' || (kart.maintenance.remainingHours != null && kart.maintenance.remainingHours <= 1);
}

export function summarizeFleetHealth(karts: KartListItem[]): FleetHealthSummary {
  return {
    total: karts.length,
    active: karts.filter((kart) => kart.status === KartStatus.ACTIVE || kart.status === KartStatus.AVAILABLE).length,
    inMaintenance: karts.filter((kart) => kart.status === KartStatus.MAINTENANCE).length,
    serviceDue: karts.filter((kart) => maintenanceTone(kart) === 'warning').length,
    serviceSoon: karts.filter((kart) => maintenanceTone(kart) === 'muted').length,
    assigned: karts.filter((kart) => Boolean(kart.assignedDriverId)).length,
  };
}

export function filterKartQueue(karts: KartListItem[], filter: KartQueueFilter) {
  switch (filter) {
    case 'service-due':
      return karts.filter((kart) => isServiceDue(kart));
    case 'maintenance':
      return karts.filter((kart) => kart.status === KartStatus.MAINTENANCE);
    case 'active':
      return karts.filter((kart) => kart.status === KartStatus.ACTIVE || kart.status === KartStatus.AVAILABLE);
    default:
      return karts;
  }
}

export function sortKartsByServiceUrgency(karts: KartListItem[]) {
  function urgencyScore(kart: KartListItem) {
    if (maintenanceTone(kart) === 'warning') {
      return 0;
    }

    if (kart.status === KartStatus.MAINTENANCE) {
      return 1;
    }

    if (maintenanceTone(kart) === 'muted') {
      return 2;
    }

    if (kart.status === KartStatus.AVAILABLE) {
      return 3;
    }

    if (kart.status === KartStatus.RETIRED) {
      return 5;
    }

    return 4;
  }

  return [...karts].sort((left, right) => {
    const scoreDiff = urgencyScore(left) - urgencyScore(right);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }

    const leftRemaining = left.maintenance.remainingHours ?? Number.POSITIVE_INFINITY;
    const rightRemaining = right.maintenance.remainingHours ?? Number.POSITIVE_INFINITY;
    return leftRemaining - rightRemaining;
  });
}

export function formatServiceLogEntry(category: string, notes: string) {
  const date = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const trimmedNotes = notes.trim();
  return trimmedNotes
    ? `[${date}] ${category} service — ${trimmedNotes}`
    : `[${date}] ${category} service logged`;
}

export function appendServiceNote(existingNotes: string | null | undefined, entry: string) {
  const trimmed = existingNotes?.trim();
  return trimmed ? `${trimmed}\n${entry}` : entry;
}
